import React, { useState, useRef } from "react";
import XLSX from "xlsx-js-style";
import moment from "moment";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import { getDateByDayOfWeek } from "../../../../utils/constanst.js";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface ConfigHorarioSemanalExportModalProps {
  open: boolean;
  onClose: () => void;
  positions: any[];
  schedules: any[];
  partners: any[];
  weekDays: string[];
  weekValue: string;
  branchName: string;
  weekName: string;
}

const ConfigHorarioSemanalExportModal: React.FC<ConfigHorarioSemanalExportModalProps> = ({
  open,
  onClose,
  positions,
  schedules,
  partners,
  weekDays,
  weekValue,
  branchName,
  weekName,
}) => {
  const [exporting, setExporting] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const getScheduleForPositionAndDay = (positionId: string, partnerId: number, day: string) => {
    const dayDate = getDateByDayOfWeek(weekValue, day);
    if (!dayDate) return null;

    const formattedDate = moment(dayDate).format("YYYY-MM-DD");
    return schedules.find(
      (schedule) =>
        schedule.position?.id === Number(positionId.split("-")[0]) &&
        schedule.partner?.id === partnerId &&
        moment(schedule.exactDate).format("YYYY-MM-DD") === formattedDate
    );
  };

  const getPartnerForPosition = (position: any): any => {
    const normalizeName = (name: string) => {
      if (!name) return "";
      return name.trim().toLowerCase().replace(/\s+\d+$/, "");
    };

    const positionNameNormalized = normalizeName(position.positionName);
    const shiftNormalized = normalizeName(position.shift);

    const matchedPartners = partners.filter((partner) => {
      if (!partner.candidate?.vacancy?.position?.name || !partner.candidate?.shift?.name) {
        return false;
      }

      const partnerPositionName = normalizeName(partner.candidate.vacancy.position.name);
      const partnerShiftName = normalizeName(partner.candidate.shift.name);

      return (
        partnerPositionName === positionNameNormalized && partnerShiftName === shiftNormalized
      );
    });

    return matchedPartners.length > 0 ? matchedPartners[0] : null;
  };

  const getRoleForDay = (position: any, partner: any, day: string): string => {
    if (!partner) return "";
    
    const schedule = getScheduleForPositionAndDay(position.id, partner.id, day);
    if (schedule) {
      const role = schedule.positionName || schedule.position?.name || "";
      return role === "N/A" ? "" : role;
    }
    return "";
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      const ws: any = XLSX.utils.aoa_to_sheet([]);

      const shiftColors: { [key: string]: string } = {
        Matutino: "F7B917",
        Intermedio: "1E90FF",
        Vespertino: "DC2C27",
      };

      const titleStyle = {
        font: { bold: true, sz: 16, color: { rgb: "1F2937" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const headerStyle = {
        fill: { fgColor: { rgb: "374151" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "4B5563" } },
          bottom: { style: "thin", color: { rgb: "4B5563" } },
          left: { style: "thin", color: { rgb: "4B5563" } },
          right: { style: "thin", color: { rgb: "4B5563" } },
        },
      };

      const shiftHeaderStyle = (shift: string) => ({
        fill: { fgColor: { rgb: shiftColors[shift] } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: shiftColors[shift] } },
          bottom: { style: "thin", color: { rgb: shiftColors[shift] } },
          left: { style: "thin", color: { rgb: shiftColors[shift] } },
          right: { style: "thin", color: { rgb: shiftColors[shift] } },
        },
      });

      const positionCellStyle = {
        fill: { fgColor: { rgb: "F3F4F6" } },
        font: { bold: true, sz: 10, color: { rgb: "1F2937" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "E5E7EB" } },
          bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        },
      };

      const partnerCellStyle = {
        fill: { fgColor: { rgb: "F3F4F6" } },
        font: { bold: true, sz: 10, color: { rgb: "1F2937" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "E5E7EB" } },
          bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        },
      };

      const dataCellStyle = (isAlt: boolean, isDescanso: boolean = false) => ({
        fill: isDescanso 
          ? { fgColor: { rgb: "F97316" } }
          : isAlt
          ? { fgColor: { rgb: "F9FAFB" } }
          : { fgColor: { rgb: "FFFFFF" } },
        font: { 
          bold: isDescanso, 
          sz: 10, 
          color: { rgb: isDescanso ? "FFFFFF" : "1F2937" } 
        },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "E5E7EB" } },
          bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        },
      });

      const titleRow = [[`Horario Semanal - ${branchName} - ${weekName}`]];
      XLSX.utils.sheet_add_aoa(ws, titleRow, { origin: "A1" });
      
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: weekDays.length + 1 } }];
      if (ws["A1"]) ws["A1"].s = titleStyle;

      const headers = ["Posición", "Colaborador", ...weekDays.map((d) => d.substring(0, 3))];
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A2" });

      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c: col });
        if (ws[cellRef]) ws[cellRef].s = headerStyle;
      }

      let rowIndex = 3;
      const shifts = ["Matutino", "Intermedio", "Vespertino"];
      
      shifts.forEach((shift) => {
        const shiftPositions = positions.filter((pos) => pos.shift === shift);
        if (shiftPositions.length === 0) return;

        const shiftRow = [shift, "", ...weekDays.map(() => "")];
        XLSX.utils.sheet_add_aoa(ws, [shiftRow], { origin: `A${rowIndex}` });
        
        for (let col = 0; col < headers.length; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c: col });
          if (ws[cellRef]) ws[cellRef].s = shiftHeaderStyle(shift);
        }

        const shiftHeaderRange = XLSX.utils.decode_range(`A${rowIndex}:${String.fromCharCode(65 + weekDays.length + 1)}${rowIndex}`);
        shiftHeaderRange.s.r = rowIndex - 1;
        shiftHeaderRange.e.r = rowIndex - 1;
        
        if (!ws["!merges"]) ws["!merges"] = [];
        ws["!merges"].push(shiftHeaderRange);
        rowIndex++;

        shiftPositions.forEach((position, posIndex) => {
          const partner = getPartnerForPosition(position);
          const partnerName = partner
            ? toCamelCase(`${partner.person.firstName} ${partner.person.lastName}`)
            : "Sin asignar";

          const row = [
            position.positionDisplayName,
            partnerName,
            ...weekDays.map((day) => {
              const role = getRoleForDay(position, partner, day);
              return role === "DESCANSO" ? "DESCANSO" : role === "N/A" ? "" : role;
            }),
          ];

          XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
          
          const isAlt = posIndex % 2 === 1;
          
          for (let col = 0; col < headers.length; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c: col });
            if (ws[cellRef]) {
              if (col === 0) {
                ws[cellRef].s = positionCellStyle;
              } else if (col === 1) {
                ws[cellRef].s = partnerCellStyle;
              } else {
                const role = getRoleForDay(position, partner, weekDays[col - 2]);
                ws[cellRef].s = dataCellStyle(isAlt, role === "DESCANSO");
              }
            }
          }

          rowIndex++;
        });
      });

      ws["!cols"] = [
        { wch: 35 },
        { wch: 30 },
        ...weekDays.map(() => ({ wch: 12 })),
      ];

      ws["!rows"] = [
        { hpt: 25 },
        { hpt: 25 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Horario Semanal");
      XLSX.writeFile(
        wb,
        `Horario_Semanal_${branchName.replace(/\s+/g, "_")}_${weekName.replace(/\s+/g, "_")}_${moment().format("YYYYMMDD_HHmm")}.xlsx`
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setExporting(false);
      onClose();
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const shiftColors: { [key: string]: string } = {
        Matutino: "#F7B917",
        Intermedio: "#1E90FF",
        Vespertino: "#DC2C27",
      };

      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.width = "1400px";
      tempDiv.style.backgroundColor = "#ffffff";
      tempDiv.style.padding = "20px";
      tempDiv.style.fontFamily = "Arial, sans-serif";

      let html = `
        <div style="margin-bottom: 20px;">
          <h1 style="text-align: center; margin: 0 0 10px 0; font-size: 24px; font-weight: 600; color: #1F2937;">
            Horario Semanal
          </h1>
          <div style="text-align: center; color: #6B7280; font-size: 14px;">
            <p style="margin: 4px 0;"><strong>Sucursal:</strong> ${branchName}</p>
            <p style="margin: 4px 0;"><strong>Semana:</strong> ${weekName}</p>
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              <th style="
                background-color: #374151;
                color: #fff;
                font-weight: 600;
                padding: 10px 12px;
                text-align: center;
                border-top: 1px solid #4B5563;
                border-bottom: 1px solid #4B5563;
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                min-width: 180px;
              ">Posición</th>
              <th style="
                background-color: #374151;
                color: #fff;
                font-weight: 600;
                padding: 10px 12px;
                text-align: center;
                border-top: 1px solid #4B5563;
                border-bottom: 1px solid #4B5563;
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                min-width: 180px;
              ">Colaborador</th>
              ${weekDays.map((day) => `
                <th style="
                  background-color: #374151;
                  color: #fff;
                  font-weight: 600;
                  padding: 10px 8px;
                  text-align: center;
                  border-top: 1px solid #4B5563;
                  border-bottom: 1px solid #4B5563;
                  font-size: 11px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  min-width: 100px;
                ">${day.substring(0, 3)}</th>
              `).join("")}
            </tr>
          </thead>
          <tbody>
      `;

      const shifts = ["Matutino", "Intermedio", "Vespertino"];
      shifts.forEach((shift) => {
        const shiftPositions = positions.filter((pos) => pos.shift === shift);
        if (shiftPositions.length === 0) return;

        html += `
          <tr>
            <td colspan="${weekDays.length + 2}" style="
              background-color: ${shiftColors[shift]};
              color: #fff;
              font-weight: 700;
              padding: 8px 12px;
              text-align: center;
              font-size: 13px;
              text-transform: uppercase;
              letter-spacing: 1px;
            ">${shift}</td>
          </tr>
        `;

        shiftPositions.forEach((position, index) => {
          const partner = getPartnerForPosition(position);
          const partnerName = partner
            ? toCamelCase(`${partner.person.firstName} ${partner.person.lastName}`)
            : "Sin asignar";

          const rowBgColor = index % 2 === 0 ? "#FFFFFF" : "#F9FAFB";

          html += `<tr style="background-color: ${rowBgColor};">`;
          html += `<td style="
            background-color: #F3F4F6;
            padding: 6px 12px;
            text-align: center;
            font-weight: 600;
            font-size: 12px;
            border-bottom: 1px solid #e5e7eb;
          ">${position.positionDisplayName}</td>`;
          html += `<td style="
            background-color: #F3F4F6;
            padding: 6px 12px;
            text-align: left;
            font-weight: 700;
            font-size: 12px;
            border-bottom: 1px solid #e5e7eb;
          ">${partnerName}</td>`;

          weekDays.forEach((day) => {
            const role = getRoleForDay(position, partner, day);
            const isDescanso = role === "DESCANSO";
            const cellContent = role === "N/A" ? "" : role;
            html += `<td style="
              padding: 4px 6px;
              text-align: center;
              font-size: 11px;
              border-bottom: 1px solid #e5e7eb;
              background-color: ${isDescanso ? "#F97316" : rowBgColor};
              color: ${isDescanso ? "#FFFFFF" : "#1F2937"};
              font-weight: ${isDescanso ? "600" : "400"};
            ">${cellContent}</td>`;
          });

          html += "</tr>";
        });
      });

      html += `
          </tbody>
        </table>
      `;

      tempDiv.innerHTML = html;
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 297;
      const pageHeight = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      const pdf = new jsPDF("landscape", "mm", "a4");
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `Horario_Semanal_${branchName.replace(/\s+/g, "_")}_${weekName.replace(/\s+/g, "_")}_${moment().format("YYYYMMDD_HHmm")}.pdf`
      );
    } catch (error) {
      console.error("Error exporting to PDF:", error);
    } finally {
      setExporting(false);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 1050,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "24px",
          width: "90%",
          maxWidth: "500px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600", color: "#1F2937" }}>
            Exportar Horario
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#9CA3AF",
              cursor: "pointer",
              padding: "0",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F3F4F6";
              e.currentTarget.style.color = "#374151";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#9CA3AF";
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <p style={{ margin: 0, color: "#6B7280", fontSize: "14px" }}>
            Selecciona el formato de exportación:
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <button
            onClick={exportToExcel}
            disabled={exporting}
            style={{
              padding: "12px 20px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#fff",
              backgroundColor: exporting ? "#9CA3AF" : "#10B981",
              border: "none",
              borderRadius: "8px",
              cursor: exporting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              if (!exporting) {
                e.currentTarget.style.backgroundColor = "#059669";
              }
            }}
            onMouseLeave={(e) => {
              if (!exporting) {
                e.currentTarget.style.backgroundColor = "#10B981";
              }
            }}
          >
            <i className="fa fa-file-excel-o" style={{ fontSize: "18px" }} />
            {exporting ? "Exportando..." : "Exportar a Excel"}
          </button>

          <button
            onClick={exportToPDF}
            disabled={exporting}
            style={{
              padding: "12px 20px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#fff",
              backgroundColor: exporting ? "#9CA3AF" : "#EF4444",
              border: "none",
              borderRadius: "8px",
              cursor: exporting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              if (!exporting) {
                e.currentTarget.style.backgroundColor = "#DC2626";
              }
            }}
            onMouseLeave={(e) => {
              if (!exporting) {
                e.currentTarget.style.backgroundColor = "#EF4444";
              }
            }}
          >
            <i className="fa fa-file-pdf-o" style={{ fontSize: "18px" }} />
            {exporting ? "Exportando..." : "Exportar a PDF"}
          </button>
        </div>

        <div
          style={{
            marginTop: "20px",
            paddingTop: "20px",
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#6B7280",
              backgroundColor: "transparent",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F9FAFB";
              e.currentTarget.style.borderColor = "#9CA3AF";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = "#D1D5DB";
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigHorarioSemanalExportModal;

